<script setup lang="ts">
import type { UserDocument } from '@/types/UserData';
import type { Parameters } from '@/types/parameters';

type Props = {
  antiRobotToken: string;
  hideNameField?: boolean;
  hideBirthdateField?: boolean;
  isRegisterScreen?: boolean;
  dynamicParams?: Parameters;
  selectedCountry?: string;
};

const {
  antiRobotToken,
  hideNameField = false,
  hideBirthdateField = false,
  isRegisterScreen = false,
  selectedCountry,
} = defineProps<Props>();

type Emits = {
  resetAntiRobotToken: [];
};

const emit = defineEmits<Emits>();

const { visitorOrUserCountry } = useParameters();
const { isRegisterRequestDocument, isRegisterRequestBirthDate } = useAuth();

const documentNumber = ref<string>('');
const documentName = ref<string>('');
const documentBirthDate = ref<string>('');

const isDocumentCPFNameDisabled = ref<boolean>(false);
const isDocumentCPFBirthDateDisabled = ref<boolean>(false);
const isDocumentCPFBirthDateVisible = ref<boolean>(false);
const isDocumentCPFNameVisible = ref<boolean>(false);

const isDocumentCPFValid = ref<boolean>(false);
const userDocument = ref<UserDocument | null>(null);

function onDocumentValidated(data: UserDocument) {
  isDocumentCPFValid.value = true;

  userDocument.value = data;
}

function onDocumentRemoved() {
  userDocument.value = null;

  emit('resetAntiRobotToken');

  setTimeout(() => {
    isDocumentCPFValid.value = false;

    documentNumber.value = '';
    documentName.value = '';
    documentBirthDate.value = '';

    isDocumentCPFBirthDateDisabled.value = false;
    isDocumentCPFNameDisabled.value = false;
    isDocumentCPFBirthDateVisible.value = false;
    isDocumentCPFNameVisible.value = false;
  }, 100);
}

watch(isDocumentCPFValid, () => {
  // teste para caso nao venha nome nem nascimento
  /* userDocument.value.name = '';
  userDocument.value.birth_date = ''; */

  if (!userDocument.value) {
    return;
  }

  const hasUserDocumentName = !!userDocument.value?.name?.length;
  const hasUserDocumentBirthDate = !!userDocument.value?.birth_date?.length;

  if (hasUserDocumentBirthDate) {
    documentBirthDate.value = userDocument.value.birth_date || '';
    isDocumentCPFBirthDateDisabled.value = true;
  } else {
    isDocumentCPFBirthDateVisible.value = true;
  }

  if (hasUserDocumentName) {
    documentName.value = userDocument.value.name || '';
    isDocumentCPFNameDisabled.value = true;
  }

  isDocumentCPFNameVisible.value = hasUserDocumentName;
});

function updateSelectedDate(formattedDate: any) {
  documentBirthDate.value = formattedDate;
}

const enableDocumentCPF = computed(() => {
  if (isRegisterScreen) {
    return selectedCountry === 'BRA' && isRegisterRequestDocument.value;
  }

  return visitorOrUserCountry.value === 'BRA';
});

const enableDocumentCURP = computed(() => {
  if (isRegisterScreen) {
    return selectedCountry === 'MEX' && isRegisterRequestDocument.value;
  }

  return visitorOrUserCountry.value === 'MEX';
});

const showBirthdateInput = computed(() => {
  return (
    !hideBirthdateField &&
    (isDocumentCPFBirthDateVisible.value || isRegisterRequestBirthDate.value)
  );
});

const showDocumentsContainer = computed(() => {
  return (
    enableDocumentCPF.value ||
    enableDocumentCURP.value ||
    showBirthdateInput.value
  );
});

const documentNumberClean = computed(() => {
  return documentNumber.value.replace(/[^\w]/g, '');
});

defineExpose({
  documentNumber,
  documentNumberClean,
  documentName,
  documentBirthDate,
});
</script>

<template>
  <ClientOnly>
    <div
      v-if="showDocumentsContainer"
      :class="$style.documentsContainer"
    >
      <template v-if="enableDocumentCPF">
        <BaseFormInputCpf
          v-model:masked="documentNumber"
          :anti-robot-token="antiRobotToken"
          hide-icon
          :label="$t('auth.register.cpf')"
          name="cpf"
          required
          @remove-document="onDocumentRemoved"
          @reset-anti-robot-token="emit('resetAntiRobotToken')"
          @validate-document="onDocumentValidated"
        />
        <BaseFormInput
          v-if="isDocumentCPFNameVisible && !hideNameField"
          id="text"
          v-model="documentName"
          autocomplete="off"
          :disabled="isDocumentCPFNameDisabled"
          :initial-value="documentName"
          :label="$t('account.name')"
          :name="'nome'"
          :required="true"
          type="nome"
        />
      </template>

      <template v-if="enableDocumentCURP">
        <BaseFormInputCurp
          v-model="documentNumber"
          hide-icon
          :label="$t('auth.register.curp')"
          name="curp"
          required
        />
      </template>

      <template v-if="showBirthdateInput">
        <BaseFormSelectBirthdate
          id="birth_date"
          v-model="documentBirthDate"
          :disabled="isDocumentCPFBirthDateDisabled"
          :icon="false"
          :initial-value="documentBirthDate"
          :label="$t('auth.register.birthdate')"
          :name="'birth_date'"
          :required="true"
          @keydown.enter.prevent=""
          @update:selected-date="updateSelectedDate"
        />
      </template>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.documentsContainer {
  @apply relative flex flex-col gap-2;
}
</style>
